// T049: Tactics Phase Handler

import type { GameState, Card, BattlefieldPosition, CardDefinition, AttackDeclaration } from '../../types';

export type TacticsError =
  | 'CARD_NOT_IN_HAND'
  | 'SLOT_OCCUPIED'
  | 'SLOT_EMPTY'
  | 'INVALID_SIDE'
  | 'UNIT_NOT_FOUND'
  | 'UNIT_TAPPED'
  | 'NO_ENEMY_IN_FRONT'
  | 'SACRIFICE_NOT_ALLOWED'
  | 'ACTION_ALREADY_USED';

export type TacticsResult =
  | { success: true; state: GameState }
  | { success: false; error: TacticsError };

/**
 * Get the slot at a given position.
 */
function getSlot(state: GameState, position: BattlefieldPosition) {
  const side = position.side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
  const line = position.row === 'front' ? side.frontLine : side.backLine;
  return line[position.column - 1];
}

/**
 * Check if a slot is empty.
 */
function isSlotEmpty(state: GameState, position: BattlefieldPosition): boolean {
  const slot = getSlot(state, position);
  return slot.unit === null;
}

/**
 * Find a unit on the battlefield by card ID.
 */
function findUnitOnBattlefield(
  state: GameState,
  cardId: string
): { unit: Card; position: BattlefieldPosition } | null {
  const sides: Array<'player1' | 'player2'> = ['player1', 'player2'];
  const rows: Array<'front' | 'back'> = ['front', 'back'];
  const columns: Array<1 | 2 | 3 | 4 | 5> = [1, 2, 3, 4, 5];

  for (const side of sides) {
    for (const row of rows) {
      for (const column of columns) {
        const position: BattlefieldPosition = { side, row, column };
        const slot = getSlot(state, position);
        if (slot.unit?.id === cardId) {
          return { unit: slot.unit, position };
        }
      }
    }
  }

  return null;
}

/**
 * Check if a unit has a specific keyword.
 */
function hasKeyword(unit: Card, keyword: string): boolean {
  return unit.keywords?.includes(keyword) ?? false;
}

/**
 * Check if there's an enemy in front of a position.
 * Takes into account combat keywords:
 * - 遠程攻擊 (ranged): back row can attack enemy front/back row
 * - 飛行 (flying): can reach enemy back row
 * - 偷襲 (sneak attack): can reach enemy back row
 */
function hasEnemyInFront(state: GameState, position: BattlefieldPosition): boolean {
  const enemySide = position.side === 'player1' ? 'player2' : 'player1';
  const slot = getSlot(state, position);
  const unit = slot.unit;

  // Check same column in enemy front line
  const enemyFrontPosition: BattlefieldPosition = {
    side: enemySide,
    row: 'front',
    column: position.column,
  };

  if (!isSlotEmpty(state, enemyFrontPosition)) {
    return true;
  }

  // Check enemy back row
  const enemyBackPosition: BattlefieldPosition = {
    side: enemySide,
    row: 'back',
    column: position.column,
  };

  const hasEnemyBack = !isSlotEmpty(state, enemyBackPosition);

  // If our unit is in front row, can naturally reach back row
  if (position.row === 'front' && hasEnemyBack) {
    return true;
  }

  // If our unit is in back row, check if it has ranged/flying/偷襲 to attack
  if (position.row === 'back' && hasEnemyBack && unit) {
    if (hasKeyword(unit, '遠程攻擊') || hasKeyword(unit, '飛行') || hasKeyword(unit, '偷襲')) {
      return true;
    }
  }

  // Back row units with ranged can also attack front row
  if (position.row === 'back' && unit) {
    if (hasKeyword(unit, '遠程攻擊')) {
      // Can attack enemy front or back
      if (!isSlotEmpty(state, enemyFrontPosition)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Update a unit's orientation on the battlefield.
 */
function updateUnitOrientation(
  state: GameState,
  position: BattlefieldPosition,
  orientation: 'upright' | 'tilted' | 'tapped'
): GameState {
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];
  const slot = newLine[position.column - 1];

  if (slot.unit) {
    newLine[position.column - 1] = {
      ...slot,
      unit: { ...slot.unit, orientation },
    };
  }

  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;

  return { ...state, battlefield: newBattlefield };
}

/**
 * Declare attack with specified units.
 * This uses the tactics action - cannot do tactical summon after this.
 */
export function declareAttack(
  state: GameState,
  playerId: string,
  attackerIds: string[]
): TacticsResult {
  // Check if action already used this phase
  if (state.tacticsActionUsed) {
    return { success: false, error: 'ACTION_ALREADY_USED' };
  }

  let newState = { ...state };
  const declarations: AttackDeclaration[] = [...state.attackDeclarations];

  for (const attackerId of attackerIds) {
    const result = findUnitOnBattlefield(newState, attackerId);

    if (!result) {
      return { success: false, error: 'UNIT_NOT_FOUND' };
    }

    const { unit, position } = result;

    // Check if unit belongs to player
    const expectedSide = playerId === state.player1.id ? 'player1' : 'player2';
    if (position.side !== expectedSide) {
      return { success: false, error: 'UNIT_NOT_FOUND' };
    }

    // Check if unit is already tapped
    if (unit.orientation === 'tapped') {
      return { success: false, error: 'UNIT_TAPPED' };
    }

    // Units summoned this turn can't attack unless they have 偷襲
    if (unit.summonedThisTurn && !hasKeyword(unit, '偷襲')) {
      return { success: false, error: 'UNIT_TAPPED' };
    }

    // Check if there's an enemy in front
    if (!hasEnemyInFront(newState, position)) {
      return { success: false, error: 'NO_ENEMY_IN_FRONT' };
    }

    // Tilt the unit to show attack declaration
    newState = updateUnitOrientation(newState, position, 'tilted');

    // Add to attack declarations
    declarations.push({
      attackerId,
      attackerPosition: position,
    });
  }

  return {
    success: true,
    state: { ...newState, attackDeclarations: declarations, tacticsActionUsed: true },
  };
}

/**
 * Tactical summon (one additional unit without sacrifice).
 * This uses the tactics action - cannot attack after this.
 */
export function tacticalSummon(
  state: GameState,
  playerId: string,
  cardId: string,
  position: BattlefieldPosition,
  definitions?: Map<string, CardDefinition>
): TacticsResult {
  // Check if action already used this phase
  if (state.tacticsActionUsed) {
    return { success: false, error: 'ACTION_ALREADY_USED' };
  }
  const isPlayer1 = playerId === state.player1.id;
  const player = isPlayer1 ? state.player1 : state.player2;
  const expectedSide = isPlayer1 ? 'player1' : 'player2';

  // Validate side
  if (position.side !== expectedSide) {
    return { success: false, error: 'INVALID_SIDE' };
  }

  // Find card in hand
  const card = player.hand.find((c) => c.id === cardId);
  if (!card) {
    return { success: false, error: 'CARD_NOT_IN_HAND' };
  }

  // Check if card requires sacrifice
  if (definitions) {
    const def = definitions.get(card.definitionId);
    if (def?.sacrificeRequired && def.sacrificeRequired > 0) {
      return { success: false, error: 'SACRIFICE_NOT_ALLOWED' };
    }
  }

  // Check slot is empty
  if (!isSlotEmpty(state, position)) {
    return { success: false, error: 'SLOT_OCCUPIED' };
  }

  // Remove from hand
  const newHand = player.hand.filter((c) => c.id !== cardId);

  // Place on battlefield
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];

  newLine[position.column - 1] = {
    ...newLine[position.column - 1],
    unit: {
      ...card,
      location: 'battlefield',
      position,
      orientation: 'upright',
      summonedThisTurn: true, // 新召喚的單位無法攻擊（除非有偷襲）
    },
  };

  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;

  if (isPlayer1) {
    return {
      success: true,
      state: {
        ...state,
        player1: { ...state.player1, hand: newHand },
        battlefield: newBattlefield,
        tacticsActionUsed: true,
      },
    };
  } else {
    return {
      success: true,
      state: {
        ...state,
        player2: { ...state.player2, hand: newHand },
        battlefield: newBattlefield,
        tacticsActionUsed: true,
      },
    };
  }
}

/**
 * Move a unit to a new position (empty slot).
 */
export function moveUnit(
  state: GameState,
  playerId: string,
  from: BattlefieldPosition,
  to: BattlefieldPosition
): TacticsResult {
  const expectedSide = playerId === state.player1.id ? 'player1' : 'player2';

  // Validate sides
  if (from.side !== expectedSide || to.side !== expectedSide) {
    return { success: false, error: 'INVALID_SIDE' };
  }

  // Check source has unit
  const fromSlot = getSlot(state, from);
  if (!fromSlot.unit) {
    return { success: false, error: 'SLOT_EMPTY' };
  }

  // Check destination is empty
  if (!isSlotEmpty(state, to)) {
    return { success: false, error: 'SLOT_OCCUPIED' };
  }

  const unit = fromSlot.unit;

  // Clear source slot
  const fromSideKey = from.side === 'player1' ? 'player1Side' : 'player2Side';
  const fromLineKey = from.row === 'front' ? 'frontLine' : 'backLine';
  const toSideKey = to.side === 'player1' ? 'player1Side' : 'player2Side';
  const toLineKey = to.row === 'front' ? 'frontLine' : 'backLine';

  const newBattlefield = { ...state.battlefield };

  // Handle same side movement
  const newFromSide = { ...newBattlefield[fromSideKey] };
  const newFromLine = [...newFromSide[fromLineKey]];
  newFromLine[from.column - 1] = { ...newFromLine[from.column - 1], unit: null };
  newFromSide[fromLineKey] = newFromLine;

  // Place at destination
  if (fromSideKey === toSideKey && fromLineKey === toLineKey) {
    // Same line
    newFromLine[to.column - 1] = {
      ...newFromLine[to.column - 1],
      unit: { ...unit, position: to },
    };
    newFromSide[fromLineKey] = newFromLine;
  } else if (fromSideKey === toSideKey) {
    // Different line, same side
    const newToLine = [...newFromSide[toLineKey]];
    newToLine[to.column - 1] = {
      ...newToLine[to.column - 1],
      unit: { ...unit, position: to },
    };
    newFromSide[toLineKey] = newToLine;
  }

  newBattlefield[fromSideKey] = newFromSide;

  return { success: true, state: { ...state, battlefield: newBattlefield } };
}

/**
 * Swap two units' positions, or move a unit to an empty slot.
 * Unlike moveUnit, this allows the destination to be occupied (swap).
 */
export function swapUnits(
  state: GameState,
  playerId: string,
  from: BattlefieldPosition,
  to: BattlefieldPosition
): TacticsResult {
  const expectedSide = playerId === state.player1.id ? 'player1' : 'player2';

  // Validate sides - both must be on player's side
  if (from.side !== expectedSide || to.side !== expectedSide) {
    return { success: false, error: 'INVALID_SIDE' };
  }

  // Same position - no-op
  if (from.row === to.row && from.column === to.column) {
    return { success: true, state };
  }

  // Check source has unit
  const fromSlot = getSlot(state, from);
  if (!fromSlot.unit) {
    return { success: false, error: 'SLOT_EMPTY' };
  }

  const toSlot = getSlot(state, to);

  // If destination is empty, delegate to moveUnit
  if (!toSlot.unit) {
    return moveUnit(state, playerId, from, to);
  }

  // Swap two units
  const unitA = fromSlot.unit;
  const unitB = toSlot.unit;

  const sideKey = from.side === 'player1' ? 'player1Side' : 'player2Side';
  const fromLineKey = from.row === 'front' ? 'frontLine' : 'backLine';
  const toLineKey = to.row === 'front' ? 'frontLine' : 'backLine';

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };

  if (fromLineKey === toLineKey) {
    // Same line swap
    const newLine = [...newSide[fromLineKey]];
    newLine[from.column - 1] = {
      ...newLine[from.column - 1],
      unit: { ...unitB, position: from },
    };
    newLine[to.column - 1] = {
      ...newLine[to.column - 1],
      unit: { ...unitA, position: to },
    };
    newSide[fromLineKey] = newLine;
  } else {
    // Cross-line swap
    const newFromLine = [...newSide[fromLineKey]];
    const newToLine = [...newSide[toLineKey]];
    newFromLine[from.column - 1] = {
      ...newFromLine[from.column - 1],
      unit: { ...unitB, position: from },
    };
    newToLine[to.column - 1] = {
      ...newToLine[to.column - 1],
      unit: { ...unitA, position: to },
    };
    newSide[fromLineKey] = newFromLine;
    newSide[toLineKey] = newToLine;
  }

  newBattlefield[sideKey] = newSide;

  return { success: true, state: { ...state, battlefield: newBattlefield } };
}

/**
 * Get all units that can attack.
 * Considers keywords: 遠程攻擊 allows back-row attacks, 偷襲 allows newly-summoned units to attack.
 */
export function getAttackableUnits(state: GameState, playerId: string): Card[] {
  const side = playerId === state.player1.id ? 'player1' : 'player2';
  const attackable: Card[] = [];

  const rows: Array<'front' | 'back'> = ['front', 'back'];
  const columns: Array<1 | 2 | 3 | 4 | 5> = [1, 2, 3, 4, 5];

  for (const row of rows) {
    for (const column of columns) {
      const position: BattlefieldPosition = { side, row, column };
      const slot = getSlot(state, position);

      if (!slot.unit) continue;
      if (slot.unit.orientation === 'tapped') continue;
      
      // Units summoned this turn can't attack unless they have 偷襲 (sneak attack)
      if (slot.unit.summonedThisTurn && !hasKeyword(slot.unit, '偷襲')) continue;

      if (hasEnemyInFront(state, position)) {
        attackable.push(slot.unit);
      }
    }
  }

  return attackable;
}

// Re-export as object for backwards compatibility
export const TacticsPhaseHandler = {
  declareAttack,
  tacticalSummon,
  moveUnit,
  swapUnits,
  getAttackableUnits,
};
